import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { ApiLink } from '../../../APILink/ApiLink';
import authHeader from '../../../Auth/authHeader';
import { errorAlert } from '../../../Components/Alerts';
import { FiscalYearContext } from '../../../FiscalYear/FiscalYearContext';
import './ExpensePieChart.scss';

const PALETTE = [
  '#c2415a',
  '#2f7d6b',
  '#2f6fb2',
  '#b7791f',
  '#7c3aed',
  '#0891b2',
  '#be185d',
  '#15803d',
  '#c2410c',
  '#4338ca',
];

const formatMoney = (value) =>
  `€ ${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const renderTooltipContent = ({ active, payload }) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const entry = payload[0];
  const pct = entry.payload.percent != null
    ? ` (${(entry.payload.percent * 100).toFixed(1)}%)`
    : '';
  return (
    <div className="epc-tooltip">
      <div className="epc-tooltip__name">{entry.name}</div>
      <div className="epc-tooltip__value">{formatMoney(entry.value)}{pct}</div>
    </div>
  );
};

export default function ExpensePieChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabledCategories, setDisabledCategories] = useState({});
  const [hasScrollableFilters, setHasScrollableFilters] = useState(false);
  const { fiscalYear } = useContext(FiscalYearContext);
  const filtersRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          ApiLink.API_URL + 'FinanceStatement/MonthlyExpenseClassReport',
          {
            headers: authHeader(),
            params: { fiscalYear },
          }
        );

        const mapped = Array.isArray(response.data)
          ? response.data
              .filter((item) => Number(item.TotalPaTVSH) > 0)
              .map((item) => ({
                name: item.KlasifikimiShpenzimit || '—',
                value: Number(item.TotalPaTVSH) || 0,
              }))
          : [];

        setData(mapped);
        setDisabledCategories({});
      } catch (error) {
        errorAlert('Gabim në kërkesë:', error);
        if (error.response?.data?.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fiscalYear]);

  const totalExpenses = data.reduce((sum, d) => sum + d.value, 0);
  const chartData = data.map((item, index) => {
    const isDisabled = Boolean(disabledCategories[item.name]);
    return {
      ...item,
      color: PALETTE[index % PALETTE.length],
      value: isDisabled ? 0 : item.value,
      isDisabled,
    };
  });
  const hasVisibleData = chartData.some((item) => item.value > 0);

  const toggleCategory = (name) => {
    setDisabledCategories((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  useEffect(() => {
    const updateScrollHint = () => {
      const element = filtersRef.current;
      if (!element) {
        setHasScrollableFilters(false);
        return;
      }

      setHasScrollableFilters(element.scrollHeight > element.clientHeight + 1);
    };

    updateScrollHint();
    window.addEventListener('resize', updateScrollHint);

    return () => window.removeEventListener('resize', updateScrollHint);
  }, [loading, chartData.length]);

  return (
    <section className="epc-panel">
      <div className="epc-panel__header">
        <div className="epc-panel__title-row">
          <h3 className="epc-panel__title">Klasifikimi i Shpenzimeve</h3>
          {!loading && totalExpenses > 0 && (
            <div className="epc-panel__total-badge">
              <span className="epc-panel__total-badge-label">Totali</span>
              <span className="epc-panel__total-badge-value">{formatMoney(totalExpenses)}</span>
            </div>
          )}
        </div>
        {/* <p className="epc-panel__subtitle">Shpërndarja sipas kategorisë</p> */}
      </div>

      <div className="epc-panel__body">
        {loading ? (
          <div className="epc-skeleton" />
        ) : data.length === 0 ? (
          <div className="epc-empty">
            Nuk ka të dhëna për klasifikimin e shpenzimeve në vitin fiskal të zgjedhur.
          </div>
        ) : (
          <div className="epc-panel__content">
            <div className="epc-panel__chart-area">
              {hasVisibleData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="46%"
                      innerRadius="42%"
                      outerRadius="72%"
                      paddingAngle={3}
                      dataKey="value"
                      isAnimationActive={false}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={renderTooltipContent} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="epc-empty epc-empty--compact">
                  Aktivizo të paktën një kategori për ta parë grafikun.
                </div>
              )}
            </div>

            <div className="epc-panel__filters-wrap">
              <ul ref={filtersRef} className="epc-legend epc-legend--filters">
                {chartData.map((item, index) => (
                  <li key={`${item.name}-${index}`} className="epc-legend__item">
                    <button
                      type="button"
                      className={`epc-legend__toggle ${item.isDisabled ? 'is-disabled' : ''}`}
                      onClick={() => toggleCategory(item.name)}
                    >
                      <span className="epc-legend__dot" style={{ backgroundColor: item.color }} />
                      <span className="epc-legend__label" title={item.name}>{item.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {hasScrollableFilters && (
                <div className="epc-panel__scroll-hint">Lëviz për më shumë</div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
